import { PlatformTypes } from '@/types/gql.generated';
import { agendaHeroDestination } from '../../addToAgendaHero/agendaHero.destination';
import { googleIntegrationDestination } from '../../addToGoogle/googleIntegration.destination';
import { outlookIntegrationDestination } from '../../addToOutlook/outlookIntegration.destination';
import { appleIcsDestination } from './appleIcs.destination';
import type { DestinationAvailability } from './availability.lib';
import { emailDestination } from './email.destination';
import { googleLinkDestination } from './googleLink.destination';
import { officeLinkDestination } from './officeLink.destination';
import { otherIcsDestination } from './otherIcs.destination';
import { outlookLinkDestination } from './outlookLink.destination';
import type {
  DestinationModule,
  IntegrationDestinationModule,
  WeblinkDestinationModule,
  WeblinkUrlKey,
} from './types';

// Each destination assembles its own DestinationModule next to its feature
// code; the registry maps platforms to those entries. Insertion order is
// meaningful: the derived platform groups below (and through them every
// picker) read declaration order.
const DESTINATIONS: Partial<Record<PlatformTypes, DestinationModule>> = {
  [PlatformTypes.AgendaHero]: agendaHeroDestination,
  [PlatformTypes.GoogleIntegration]: googleIntegrationDestination,
  [PlatformTypes.OutlookIntegration]: outlookIntegrationDestination,
  [PlatformTypes.Email]: emailDestination,
  [PlatformTypes.GoogleLink]: googleLinkDestination,
  [PlatformTypes.AppleIcs]: appleIcsDestination,
  [PlatformTypes.OutlookLink]: outlookLinkDestination,
  [PlatformTypes.OfficeLink]: officeLinkDestination,
  [PlatformTypes.OtherIcs]: otherIcsDestination,
};

export const getDestination = (
  platform: PlatformTypes
): DestinationModule | undefined => {
  return DESTINATIONS[platform];
};

export const getRegisteredDestinations = (): DestinationModule[] =>
  Object.values(DESTINATIONS);

const isIntegrationDestination = (
  module: DestinationModule
): module is IntegrationDestinationModule => Boolean(module.integration);

export const getIntegrationDestinations = (): IntegrationDestinationModule[] =>
  getRegisteredDestinations().filter(isIntegrationDestination);

export const getIntegrationDestination = (
  platform: PlatformTypes
): IntegrationDestinationModule | undefined => {
  const module = getDestination(platform);
  return module && isIntegrationDestination(module) ? module : undefined;
};

/**
 * Resolve a weblink destination by its server url key — the reverse lookup
 * surfaces speaking the weblink vocabulary (the calendar-feed grid) use
 * instead of keeping their own platform map. Keys are unique per module
 * (registry invariant test).
 */
export const getWeblinkDestination = (
  urlKey: WeblinkUrlKey
): WeblinkDestinationModule | undefined =>
  getRegisteredDestinations().find(
    (module): module is WeblinkDestinationModule =>
      module.weblink?.urlKey === urlKey
  );

// ---------------------------------------------------------------------------
// Platform groups, derived from declared behavior in registry order. The
// semantic comments lived on the literal arrays these replace
// (multiExport/types.ts); the membership is pinned against those literals by
// destinations/registry.test.ts.
// ---------------------------------------------------------------------------

const platformsWhere = (
  predicate: (module: DestinationModule) => boolean
): PlatformTypes[] =>
  getRegisteredDestinations()
    .filter(predicate)
    .map((module) => module.platform);

/**
 * Integration-backed destinations: they write to a connected calendar, so they
 * support select-mode/bulk export and open a drawer rather than firing a
 * one-shot weblink.
 */
export const MULTI_EXPORT_PLATFORMS = platformsWhere(
  (module) => module.behavior === 'sync'
);

/** One-time-copy weblinks offered by destination pickers. */
export const PER_EVENT_PLATFORMS = platformsWhere(
  (module) =>
    module.behavior === 'copy' &&
    Boolean(module.weblink) &&
    !module.isPickerHidden
);

/** Copy destinations that open a compose drawer instead of a weblink (Email). */
export const DRAWER_PLATFORMS = platformsWhere(
  (module) => module.behavior === 'copy' && !module.weblink
);

/** Subscribing to the whole schedule rather than adding events one at a time. */
export const FEED_PLATFORMS = platformsWhere(
  (module) => module.behavior === 'feed'
);

export const ALL_PLATFORMS = [
  ...MULTI_EXPORT_PLATFORMS,
  ...DRAWER_PLATFORMS,
  ...PER_EVENT_PLATFORMS,
];

/**
 * The one-shot weblink destinations (server-built add link opened in a new
 * tab), in menu display order. Deliberately flag-independent: weblink menus
 * never swap in the Outlook integration, whose weblink URL doesn't exist.
 */
export const WEBLINK_PLATFORMS = platformsWhere((module) =>
  Boolean(module.weblink)
);

/**
 * The destinations offered for exporting events, in display order. Shared by
 * every surface that lets a reader pick a calendar — the export sidebar and the
 * event modal's Add-to-Calendar panel — so the two can't offer different lists.
 * Email sits with the other per-event flows: it composes an invite per event
 * rather than opening a weblink.
 */
export const EVENT_EXPORT_PLATFORMS = [
  ...MULTI_EXPORT_PLATFORMS,
  ...DRAWER_PLATFORMS,
  ...PER_EVENT_PLATFORMS,
  ...FEED_PLATFORMS,
];

// ---------------------------------------------------------------------------
// Availability resolution. These read the registry, so they live here rather
// than in availability.lib — the entries import that module at init, and a
// registry import from it would close a cycle that leaves whichever module
// initialized first holding undefined bindings.
// ---------------------------------------------------------------------------

const isAvailable = (
  platform: PlatformTypes,
  availability: DestinationAvailability
): boolean => availability[platform] ?? true;

/**
 * Which of `candidates` the workspace actually offers, in candidate order:
 * unavailable destinations drop out, and an available destination replaces the
 * first candidate it stands in for (the Outlook integration takes the Outlook
 * weblink's slot, so menu order does not shift when the flag flips), deduped
 * keep-first so the static lists that already carry the integration can't
 * yield it twice.
 */
export const resolveAvailablePlatforms = (
  candidates: PlatformTypes[],
  availability: DestinationAvailability
): PlatformTypes[] => {
  const modules = getRegisteredDestinations();
  const seen = new Set<PlatformTypes>();

  return candidates.flatMap((candidate) => {
    const standIn = modules.find(
      (module) =>
        isAvailable(module.platform, availability) &&
        module.standsInFor?.includes(candidate)
    );
    const resolved = standIn ? standIn.platform : candidate;

    if (!isAvailable(resolved, availability) || seen.has(resolved)) {
      return [];
    }
    seen.add(resolved);
    return [resolved];
  });
};

/**
 * Degrade a persisted/pinned platform that outlived its module's availability:
 * unavailable destinations land on their declared fallbackPlatform (the
 * Outlook integration falls back to the Outlook weblink, which shares its
 * label and icon so nothing flickers while the flag resolves) or null when
 * no fallback exists (a disabled Agenda Hero selection is dropped).
 *
 * Callers pass a scoped availability record covering just the platforms whose
 * degrade they own, so a surface can't accidentally widen its semantics.
 */
export const degradeUnavailablePlatform = (
  platform: PlatformTypes | null,
  availability: DestinationAvailability
): PlatformTypes | null => {
  if (!platform || isAvailable(platform, availability)) {
    return platform;
  }
  return getDestination(platform)?.fallbackPlatform ?? null;
};

export const isMultiExportPlatform = (platform: PlatformTypes): boolean =>
  MULTI_EXPORT_PLATFORMS.includes(platform);

export const isDrawerPlatform = (platform: PlatformTypes): boolean =>
  DRAWER_PLATFORMS.includes(platform);

export const isPerEventPlatform = (platform: PlatformTypes): boolean =>
  PER_EVENT_PLATFORMS.includes(platform);

export const isFeedPlatform = (platform: PlatformTypes): boolean =>
  FEED_PLATFORMS.includes(platform);
